import { useTranslations } from '@power-core/toolkit';
import { useEffect } from 'react';

import { useAppContext } from '@/modules/app-context/use-app-context';
import { useDocumentTypesInit } from '@/modules/document-type/hooks/use-document-types-init';
import { useReleaseNotesApi } from '@/modules/release-notes/hooks/use-release-notes-api';
import type { SearchableDropdownItemProps } from '@/ui-components/dropdown/types';
import { useAuth } from '@power-core/toolkit';

export const useSmartCardsInit = () => {
	const t = useTranslations();
	const { auth } = useAuth();
	const { setToolsForSelect, setDocumentTypesURLS, setReleaseNotesStatus, setDocumentTypes } = useAppContext();
	const { documentTypes } = useDocumentTypesInit();
	const { checkIfUserViewedLastNote } = useReleaseNotesApi();

	useEffect(() => {
		if (!documentTypes?.length) return;

		setDocumentTypes(documentTypes);

		const fetchReleaseNotesStatus = async () => {
			const results = await Promise.all(
				documentTypes.map(async (card) => {
					try {
						const response = await checkIfUserViewedLastNote.mutateAsync({ documentTypeDocId: card.documentId });
						return { documentId: card.documentId, isViewedLastNote: response.data };
					} catch (error) {
						console.error(`Error with checking Release Notes for ${card.documentId}:`, error);
						return { documentId: card.documentId, isViewedLastNote: false };
					}
				}),
			);

			const newStatus = results.reduce(
				(acc, { documentId, isViewedLastNote }) => {
					acc[documentId] = isViewedLastNote;
					return acc;
				},
				{} as Record<string, boolean>,
			);

			setReleaseNotesStatus(newStatus);
		};

		if (auth?.data?.user?.email) fetchReleaseNotesStatus();

		const toolsForSelect = documentTypes.reduce((acc, cur) => {
			if (cur.title) {
				acc.push({
					value: cur.title,
					label: t(cur.title) ?? '',
				});
			}
			return acc;
		}, [] as SearchableDropdownItemProps[]);

		const documentTypesURLS = documentTypes.reduce(
			(acc, cur) => {
				if (cur.title && cur.url) {
					acc[cur.title] = cur.url;
				}
				return acc;
			},
			{} as Record<string, string>,
		);

		setDocumentTypesURLS(documentTypesURLS);
		setToolsForSelect(toolsForSelect);
	}, [documentTypes?.length]);
};
